import { create } from 'zustand';
import { Pipeline } from '../types/pipeline';
import { DEFAULT_PAGE, objectPage, pageForPath, pathForPage, resolvePage } from '../shell/routes';
import { leaveThen } from '../shell/leaveGuard';
import { locationSnapshot, noteLocation, restoreLocation, writeLocation } from '../shell/resourceRoutes';

export interface BreadcrumbItem {
  label: string;
  page?: string;
  icon?: string;
}

interface NavigationStore {
  currentPage: string;
  pendingPipeline: Partial<Pipeline> | null;
  pendingAppId: string | null;
  navigateToApp: (id: string, kind: 'dashboard' | 'app' | 'document' | 'solution') => void;
  pendingSettingsTab: string | null;
  pendingConnectorType: string | null;
  pendingConnectorId: string | null;
  breadcrumbs: BreadcrumbItem[];
  navigateTo: (page: string, pendingPipeline?: Partial<Pipeline>) => void;
  // Deep-link to a specific Settings tab (e.g. 'alerts') from anywhere.
  navigateToSettings: (tab: string) => void;
  consumeSettingsTab: () => string | null;
  // Deep-link to Connectors with the "add connector" modal pre-opened at a
  // given platform type id (e.g. from Discovery's recommended-connectors panel).
  navigateToConnector: (connectorType: string) => void;
  consumePendingConnectorType: () => string | null;
  // Deep-link to Connectors with one EXISTING connector's detail panel open
  // (e.g. from a bot, to the numbers that mirror under it). By id rather than
  // by type: the target is one row, not a kind of row.
  navigateToConnectorInstance: (connectorId: string) => void;
  consumePendingConnectorId: () => string | null;
  consumePendingPipeline: () => Partial<Pipeline> | null;
  setBreadcrumbs: (items: BreadcrumbItem[]) => void;
  /** Like navigateTo, but replaces the history entry instead of adding one —
   *  for redirects the user did not ask for (a page they cannot access). */
  redirectTo: (page: string) => void;
  /** Open one record full page at /o/<type>/<record>. */
  navigateToObject: (objectTypeId: string, recordId: string) => void;
  /** Re-reads the URL after the browser's back/forward buttons. */
  syncFromLocation: () => void;
}

// ── URL sync ────────────────────────────────────────────────────────────────
// The store stays the source of truth; the URL mirrors (currentPage,
// pendingAppId). Writes are skipped when the path already matches, so a
// popstate that set the state from the URL never pushes it back.

const hasWindow = typeof window !== 'undefined';

const initial = (() => {
  if (!hasWindow) return null;
  return pageForPath(window.location.pathname);
})();

function writeUrl(page: string, appId: string | null, mode: 'push' | 'replace') {
  if (!hasWindow) return;
  const path = pathForPage(page, appId);
  if (window.location.pathname === path) return;
  // Already somewhere inside this page (a module that keeps its own sub-paths,
  // like Academy's /academy/tracks/...): leave the address as it is.
  const here = pageForPath(window.location.pathname);
  if (!appId && here && here.page === page && !here.appId) return;
  try {
    if (mode === 'push') window.history.pushState({ page, appId }, '', path);
    // A redirect keeps the query string: `?tour=1` and other one-shot
    // parameters are read after the shell picks its landing page.
    else window.history.replaceState({ page, appId }, '', path + window.location.search + window.location.hash);
  } catch { /* history can throw in sandboxed iframes; the store still works */ }
  noteLocation();
}

export const useNavigationStore = create<NavigationStore>((set, get) => ({
  currentPage: initial?.page ?? DEFAULT_PAGE,
  pendingPipeline: null,
  pendingAppId: initial?.appId ?? null,
  navigateToApp: (id, kind) => leaveThen(() => {
    const page = kind === 'app' ? 'apps-app' : kind === 'document' ? 'documents' : kind === 'solution' ? 'solution-design' : 'apps';
    set({ currentPage: page, pendingAppId: id, breadcrumbs: [] });
    writeUrl(page, id, 'push');
  }),
  pendingSettingsTab: null,
  pendingConnectorType: null,
  pendingConnectorId: null,
  breadcrumbs: [],

  navigateTo: (rawPage, pendingPipeline) => leaveThen(() => {
    const page = resolvePage(rawPage);
    set({ currentPage: page, pendingPipeline: pendingPipeline ?? null, pendingAppId: null, breadcrumbs: [] });
    writeUrl(page, null, 'push');
  }),

  redirectTo: (rawPage) => {
    const page = resolvePage(rawPage);
    set({ currentPage: page, pendingAppId: null, breadcrumbs: [] });
    writeUrl(page, null, 'replace');
  },

  navigateToObject: (objectTypeId, recordId) => leaveThen(() => {
    const page = objectPage(objectTypeId, recordId);
    set({ currentPage: page, pendingAppId: null, breadcrumbs: [] });
    writeUrl(page, null, 'push');
  }),

  syncFromLocation: () => {
    if (!hasWindow) return;
    const parsed = pageForPath(window.location.pathname);
    const page = parsed?.page ?? DEFAULT_PAGE;
    const appId = parsed?.appId ?? null;
    const { currentPage, pendingAppId } = get();
    if (page === currentPage && appId === pendingAppId) return;
    // The browser already moved the address: a refusal puts it back, and
    // leaving later moves it forward again.
    const target = locationSnapshot();
    leaveThen(() => {
      writeLocation(target, 'push', page);
      set({ currentPage: page, pendingAppId: appId, breadcrumbs: [] });
      noteLocation();
    }, () => restoreLocation(currentPage));
  },

  navigateToSettings: (tab) => leaveThen(() => {
    set({ currentPage: 'settings', pendingSettingsTab: tab, breadcrumbs: [] });
    writeUrl('settings', null, 'push');
  }),

  consumeSettingsTab: () => {
    const { pendingSettingsTab } = get();
    // Only write when there is something to clear: callers consume during
    // render, and a no-op set still notifies every subscriber (React warns).
    if (pendingSettingsTab !== null) set({ pendingSettingsTab: null });
    return pendingSettingsTab;
  },

  navigateToConnector: (connectorType) => leaveThen(() => {
    set({ currentPage: 'connectors', pendingConnectorType: connectorType, breadcrumbs: [] });
    writeUrl('connectors', null, 'push');
  }),

  consumePendingConnectorType: () => {
    const { pendingConnectorType } = get();
    // Only write when there is something to clear: callers consume during
    // render, and a no-op set still notifies every subscriber (React warns).
    if (pendingConnectorType !== null) set({ pendingConnectorType: null });
    return pendingConnectorType;
  },

  navigateToConnectorInstance: (connectorId) => leaveThen(() => {
    set({ currentPage: 'connectors', pendingConnectorId: connectorId, breadcrumbs: [] });
    writeUrl('connectors', null, 'push');
  }),

  // Consumed ONCE. A pending id that survived would re-open that panel every
  // time an operator navigated back to Connectors, over whatever they had
  // selected since.
  consumePendingConnectorId: () => {
    const { pendingConnectorId } = get();
    // Only write when there is something to clear: callers consume during
    // render, and a no-op set still notifies every subscriber (React warns).
    if (pendingConnectorId !== null) set({ pendingConnectorId: null });
    return pendingConnectorId;
  },

  consumePendingPipeline: () => {
    const { pendingPipeline } = get();
    // Only write when there is something to clear: callers consume during
    // render, and a no-op set still notifies every subscriber (React warns).
    if (pendingPipeline !== null) set({ pendingPipeline: null });
    return pendingPipeline;
  },

  setBreadcrumbs: (items) => {
    set({ breadcrumbs: items });
  },
}));
